import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { Bell, MessageSquare, ClipboardList, FileText, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationBell = ({ onNavigate }) => {
  const [counts, setCounts] = useState({ feedback: 0, tasks: 0, total: 0 });
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const fetchCounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-counts`);
      setCounts(res.data);
    } catch { /* silent */ }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications/list`);
      setNotifications(res.data.notifications || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, [fetchCounts]);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const typeIcon = (type) => {
    if (type === 'feedback') return <MessageSquare className="w-4 h-4 text-indigo-500" />;
    if (type === 'task') return <ClipboardList className="w-4 h-4 text-amber-500" />;
    return <FileText className="w-4 h-4 text-green-500" />;
  };

  const handleClick = (n) => {
    setOpen(false);
    if (onNavigate && n.navigate_to) onNavigate(n.navigate_to);
  };

  return (
    <div className="relative" ref={ref} data-testid="notification-bell">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
        data-testid="notification-bell-btn"
      >
        <Bell className="w-5 h-5 text-gray-600" />
        {counts.total > 0 && (
          <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full" data-testid="notification-count">
            {counts.total > 99 ? '99+' : counts.total}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
            <h3 className="font-semibold text-sm text-gray-800">Notifications</h3>
            <div className="flex items-center gap-2">
              {counts.feedback > 0 && <Badge className="bg-indigo-100 text-indigo-700 text-xs">{counts.feedback} feedback</Badge>}
              {counts.tasks > 0 && <Badge className="bg-amber-100 text-amber-700 text-xs">{counts.tasks} tasks</Badge>}
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-8 text-center text-gray-400 text-sm">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                No notifications
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={`${n.type}-${n.id}`}
                  onClick={() => handleClick(n)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors flex items-start gap-3 ${!n.read ? 'bg-blue-50/50' : ''}`}
                  data-testid={`notif-${n.type}-${n.id}`}
                >
                  <div className="mt-0.5 flex-shrink-0">{typeIcon(n.type)}</div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm truncate ${!n.read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>{n.title}</p>
                    <p className="text-xs text-gray-500 truncate">{n.subtitle}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{n.created_at ? new Date(n.created_at).toLocaleDateString() : ''}</p>
                  </div>
                  {!n.read && <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
